// "Sarees under ₹2,000" — a photo-led block on the store home that opens the store's own Sarees
// listing capped at that price (?under=2000, applied without a filter tag). There is no page of its own: the
// listing (HealthConcernList) already reads `price` from the URL and shows "Under ₹2,000".
//
// WHICH CATEGORY
// --------------
// Sarees is found in get_home_page's category groups by NAME, never by id — hct_id is an IDENTITY
// column and differs per environment (Utils/sareeGate.js explains). A store with no Sarees
// category gets no banner and makes no request.
//
// ONE REQUEST, THREE ANSWERS
// --------------------------
// The banner asks the listing endpoint itself (all_store_products_by_hct, maxPrice=2000, the same
// gender hint the click will set) for a handful of pieces. That one reply decides:
//   - the photos: up to three IN-STOCK sarees (the listing also returns sold-out ones, badged);
//   - whether to render at all: no in-stock saree at ₹2,000 or less -> nothing, so the banner can
//     never open an empty or sold-out page;
//   - the currency: prices arrive in the shopper's display currency, and 2000 would mean $2,000
//     to an international shopper, so anything but INR -> nothing.
// Until the reply lands it renders nothing rather than a placeholder block: a block that might
// then disappear is worse than a block that appears.
//
// COPY
// ----
// Deliberately no "handloom"/"handwoven" — not every saree a store lists is handloom (reviewed
// 26 Oct). The owner's name is the only personal touch, and the line drops it when unknown.

import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { post } from "../../Services/api.service";
import { Paths } from "../../Services/EndPoints";
import { createSlug } from "../../Utils/HelperFunctions";
import { formatCardAmount } from "../../Utils/cardPrice";
import {
  SAREES_UNDER_PRICE,
  SAREES_UNDER_PARAM,
  isSareeCategoryName,
} from "../../Utils/sareeGate";
import { genderForVertical, setGenderHint } from "../search/categoryNav";
import { Imagekit } from "../../Pages/CommonPages/ImageKit";
import SareesUnderBannerDesktop, {
  pickDesktopDesign,
} from "./SareesUnderBannerDesktop";

// Fetch a few more than we show: sold-out pieces are dropped before picking the photos.
const FETCH_SIZE = 6;
const PHOTO_COUNT = 3;
// Phone/tablet (below xl): photos fill the whole block, the first one widest, text over them.
// Desktop (xl, 1280px+) is one of three separate designs in SareesUnderBannerDesktop.jsx.
// Literal class strings so Tailwind's scanner keeps them.
const COLUMNS = {
  1: "grid-cols-1",
  2: "grid-cols-[1.3fr_1fr]",
  3: "grid-cols-[1.3fr_1fr_1fr]",
};

/** The store's Sarees sub-category from get_home_page's groups, or null. */
export function findSareesCategory(groups) {
  for (const group of Array.isArray(groups) ? groups : []) {
    for (const sub of group?.healthConcerns || []) {
      if (sub?.id && isSareeCategoryName(sub?.englishName)) {
        return {
          id: sub.id,
          slug: createSlug(sub.englishName),
          vertical: createSlug(group?.englishName) || "uncategorized",
        };
      }
    }
  }
  return null;
}

const postFetcher = (url) => post(url).then((res) => res?.data);

/**
 * @param {Array}  categoryGroups - get_home_page `healthConcerns` (the home category groups)
 * @param {string} [ownerName]    - the owner's first name, already resolved by MyStore
 */
export default function SareesUnderBanner({ categoryGroups, ownerName = "" }) {
  const { t } = useTranslation();
  const storeDetails = useSelector((s) => s.storeDetails);
  const sarees = findSareesCategory(categoryGroups);

  let key = null;
  if (sarees && Paths?.view_categories_new) {
    const p = new URLSearchParams({
      healthConcernIds: sarees.id,
      maxPrice: SAREES_UNDER_PRICE,
      pageNo: 1,
      pageSize: FETCH_SIZE,
    });
    const lang = localStorage.getItem("i18nextLng");
    if (lang) p.set("language", lang);
    const gender = genderForVertical(sarees.vertical);
    if (gender) p.set("gender", gender);
    key = `${Paths.view_categories_new}?${p.toString()}`;
  }

  const { data } = useSWR(key, postFetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  if (!sarees || !data) return null;
  if ((data.currency || "INR") !== "INR") return null;
  const products = data.healthConcernProduct?.products;
  const photos = (Array.isArray(products) ? products : [])
    .filter((p) => !p?.isOutOfStock && (p?.productThumbnail || p?.productUrl))
    .slice(0, PHOTO_COUNT);
  if (photos.length === 0) return null;

  const price = formatCardAmount(SAREES_UNDER_PRICE);
  const href = `/shop/${sarees.vertical}/${sarees.slug}/${sarees.id}?${SAREES_UNDER_PARAM}=${SAREES_UNDER_PRICE}`;
  const sub = ownerName
    ? t("sareesUnder.sub", { name: ownerName })
    : t("sareesUnder.subNoName");

  return (
    <Link
      to={href}
      // The listing reads this hint back as the gender filter, exactly as a category tap sets it.
      onClick={() => setGenderHint(sarees.vertical)}
      aria-label={t("sareesUnder.aria", { price })}
      className="block mx-4 mb-[16px] rounded-[14px] overflow-hidden hover:opacity-[0.97] transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ink-900"
    >
      {/* Desktop: one of three designs, the same one for this store on every visit. */}
      <SareesUnderBannerDesktop
        design={pickDesktopDesign(storeDetails?.storeId, storeDetails?.storeName)}
        photos={photos}
        price={price}
        eyebrow={t("sareesUnder.eyebrow")}
        sub={sub}
        cta={t("sareesUnder.cta")}
      />

      {/* Phone/tablet layout (hidden from xl). Images are lazy, so the layout that is hidden on
          this screen never downloads its photos. */}
      <div className="relative h-[214px] md:h-[260px] bg-ink-900 xl:hidden">
        {/* Photos edge to edge, no gap between them (a gap showed the dark ground as lines). */}
        <div
          className={`absolute inset-0 grid ${COLUMNS[photos.length]}`}
          aria-hidden="true"
        >
          {photos.map((p) => (
            <div key={p.skuId || p.productId} className="relative overflow-hidden">
              <Imagekit
                IMGName={p.productThumbnail || p.productUrl}
                alt=""
                width={400}
                sizes="(max-width: 600px) 45vw, 300px"
                className="absolute inset-0 !w-full !h-full !object-cover !object-top"
              />
            </div>
          ))}
        </div>

        {/* Shade under the text so white copy reads on any photo. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,16,24,0) 30%, rgba(15,16,24,0.8) 100%)",
          }}
          aria-hidden="true"
        />

        {/* Copy and button on one row over the photos' bottom edge. */}
        <div className="absolute left-4 right-[14px] bottom-[14px] flex items-end justify-between gap-[10px]">
          <div className="min-w-0 flex flex-col gap-[3px]">
            <span className="text-[10.5px] leading-[1.5] tracking-[0.14em] font-[500] text-[#FFD2BF]">
              {t("sareesUnder.eyebrow")}
            </span>
            <h2 className="m-0 text-[21px] md:text-[26px] leading-[1.2] font-[500] text-white">
              {t("sareesUnder.heading", { price })}
            </h2>
            <span className="text-[12px] md:text-[13px] leading-[1.4] text-white/85">
              {sub}
            </span>
          </div>
          {/* A styled span inside the one link, never a second (nested) link or a button. */}
          <span className="flex-none inline-flex items-center gap-[6px] bg-white text-ink-900 rounded-full px-[14px] py-[8px] text-[12.5px] font-[500]">
            {t("sareesUnder.cta")}
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14m-6-6 6 6-6 6" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}
